import { useCallback, useRef } from 'react';
import { useLoading } from '../Components/GlobalLoading/LoadingContext';

/**
 * Enhanced loading state manager with debouncing for optimal UX
 * Implements the loading state best practices:
 * - No loading state for instant operations (<100ms)
 * - Debounced loading for short delays (100-300ms)
 * - Immediate loading for long operations (>300ms)
 * - Always show loading for auth redirects
 */
export const useLoadingManager = () => {
  const { showLoading, hideLoading } = useLoading();
  const loadingTimers = useRef(new Map());
  const operationStartTimes = useRef(new Map());

  /**
   * Start a loading operation with intelligent debouncing
   * @param {string} operationId - Unique identifier for the operation
   * @param {string} message - Loading message to display
   * @param {Object} options - Configuration options
   * @param {number} options.debounceTime - Debounce delay in ms (default: 100)
   * @param {boolean} options.immediate - Show loading immediately (for auth/critical ops)
   * @param {number} options.minDuration - Minimum time to show loading (prevents flicker)
   */
  const startLoading = useCallback((operationId, message = 'Loading...', options = {}) => {
    const {
      debounceTime = 100,
      immediate = false,
      minDuration = 500
    } = options;

    // Cancel any existing timer for this operation
    if (loadingTimers.current.has(operationId)) {
      clearTimeout(loadingTimers.current.get(operationId));
    }

    // Record operation start time
    operationStartTimes.current.set(operationId, Date.now());

    // Show loading immediately for critical operations
    if (immediate) {
      showLoading(message, operationId);
      return;
    }

    // Debounce loading state for non-critical operations
    const timerId = setTimeout(() => {
      showLoading(message, operationId);
      loadingTimers.current.delete(operationId);
    }, debounceTime);

    loadingTimers.current.set(operationId, timerId);
  }, [showLoading]);

  /**
   * End a loading operation with minimum duration enforcement
   * @param {string} operationId - Unique identifier for the operation
   * @param {Object} options - Configuration options
   * @param {number} options.minDuration - Minimum time to show loading (prevents flicker)
   */
  const endLoading = useCallback((operationId, options = {}) => {
    const { minDuration = 500 } = options;

    // Cancel any pending timer
    if (loadingTimers.current.has(operationId)) {
      clearTimeout(loadingTimers.current.get(operationId));
      loadingTimers.current.delete(operationId);
      
      // If timer was cancelled before showing loading, just return
      hideLoading(operationId);
      operationStartTimes.current.delete(operationId);
      return;
    }

    // Calculate how long the loading state has been shown
    const startTime = operationStartTimes.current.get(operationId);
    if (!startTime) {
      hideLoading(operationId);
      return;
    }

    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, minDuration - elapsedTime);

    // If we need to maintain minimum duration, delay hiding
    if (remainingTime > 0) {
      setTimeout(() => {
        hideLoading(operationId);
        operationStartTimes.current.delete(operationId);
      }, remainingTime);
    } else {
      hideLoading(operationId);
      operationStartTimes.current.delete(operationId);
    }
  }, [hideLoading]);

  /**
   * Execute an async operation with intelligent loading state management
   * @param {Function} operation - Async function to execute
   * @param {Object} config - Loading configuration
   * @returns {Promise} Result of the operation
   */
  const withLoading = useCallback(async (operation, config = {}) => {
    const {
      operationId = `op_${Date.now()}`,
      message = 'Loading...',
      immediate = false,
      debounceTime = 100,
      minDuration = 500
    } = config;

    try {
      startLoading(operationId, message, { immediate, debounceTime, minDuration });
      const result = await operation();
      return result;
    } finally {
      endLoading(operationId, { minDuration });
    }
  }, [startLoading, endLoading]);

  /**
   * Cancel all pending loading operations
   */
  const cancelAllLoading = useCallback(() => {
    loadingTimers.current.forEach(timerId => clearTimeout(timerId));
    loadingTimers.current.clear();
    operationStartTimes.current.clear();
  }, []);

  return {
    startLoading,
    endLoading,
    withLoading,
    cancelAllLoading
  };
};

// Predefined loading configurations for common scenarios
export const LOADING_CONFIGS = {
  // Instant navigation - no loading state
  INSTANT_NAV: {
    debounceTime: 0,
    immediate: false,
    minDuration: 0
  },
  
  // Short operations - debounced loading
  SHORT_OPERATION: {
    debounceTime: 200,
    immediate: false,
    minDuration: 500
  },
  
  // Long operations - immediate loading
  LONG_OPERATION: {
    debounceTime: 0,
    immediate: true,
    minDuration: 800
  },
  
  // Authentication - always immediate
  AUTH: {
    debounceTime: 0,
    immediate: true,
    minDuration: 1000
  }
};